import React from 'react';
import { Globe } from 'lucide-react';

/**
 * RevenueShareTerms — full Search.Inc Revenue Share screen.
 *
 * Used in two places:
 *   • MenuOverlay → "Revenue Share Terms" button
 *   • ControlCentre → "Revenue Share Tracker" module
 *
 * Self-contained panel including back button, hero, globe card, revenue
 * earnings table, and Value Credits rules side panel.
 */
export const RevenueShareTerms = ({ onBack, isMobile = false }) => {
  return (
    <div
      className={`relative z-10 w-full ${isMobile ? 'mx-2 max-h-[95vh]' : 'max-w-6xl mx-4 max-h-[90vh]'} rounded-2xl flex flex-col overflow-hidden`}
      style={{
        background: 'linear-gradient(135deg, rgba(10, 15, 30, 0.98) 0%, rgba(20, 30, 50, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(100, 200, 255, 0.15)',
      }}
      data-testid="revenue-terms-panel"
    >
      <button
        onClick={onBack}
        className="absolute top-5 right-6 text-gray-300 hover:text-white transition-colors text-xl z-20"
        data-testid="revenue-terms-back-btn"
      >
        &#x21A9;
      </button>

      <div className="overflow-y-auto policy-content">
        {/* Hero */}
        <div className={`relative ${isMobile ? 'px-4 pt-6 pb-4' : 'px-8 pt-10 pb-6'} text-center overflow-hidden`}>
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-transparent to-transparent"></div>
          {!isMobile && (
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          )}
          <div className="relative">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-5xl'} font-bold mb-3`}>
              <span className="text-cyan-400">Search.Inc</span>{' '}
              <span className="text-cyan-400">Revenue Share</span>
            </h1>
            <p className={`${isMobile ? 'text-sm' : 'text-xl'} text-cyan-300/80 font-light`}>
              The only Revenue Share of its kind in the world
            </p>
            <p className={`text-gray-400 mt-3 max-w-2xl mx-auto ${isMobile ? 'text-xs' : ''}`}>
              As we grow, so do our most engaged users. At 4% market share, almost{' '}
              <span className="text-cyan-300 font-semibold">$1 Billion</span> will be shared amongst 80k users.
            </p>
          </div>
        </div>

        <div className={`${isMobile ? 'px-3 pb-6' : 'px-8 pb-8'}`}>
          <div className={isMobile ? 'flex flex-col gap-4' : 'grid grid-cols-12 gap-6'}>
            {/* Globe card */}
            <div className={isMobile ? '' : 'col-span-3'}>
              <div
                className={`relative ${isMobile ? 'min-h-[200px]' : 'h-full min-h-[380px]'} rounded-2xl overflow-hidden flex flex-col items-center justify-center`}
                style={{ background: 'linear-gradient(180deg, #0066cc 0%, #003366 100%)' }}
              >
                <div className="text-center mb-4">
                  <p className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-white`}>5.5 Billion</p>
                  <p className={`text-cyan-300/80 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    Approximate number of<br />
                    internet users on planet Earth
                  </p>
                </div>
                <div className={`${isMobile ? 'w-24 h-24' : 'w-40 h-40'} rounded-full border-2 border-cyan-400/30 relative`}>
                  <div className="absolute inset-2 rounded-full border border-cyan-400/20"></div>
                  <div className="absolute inset-4 rounded-full border border-cyan-400/10"></div>
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-cyan-400/20"></div>
                  <div className="absolute top-0 bottom-0 left-1/2 w-px bg-cyan-400/20"></div>
                  <Globe className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${isMobile ? 'w-8 h-8' : 'w-14 h-14'} text-cyan-400/60`} />
                </div>
              </div>
            </div>

            {/* Revenue table */}
            <div className={isMobile ? '' : 'col-span-5'}>
              <div className={`bg-gray-900/50 rounded-2xl ${isMobile ? 'p-4' : 'p-6'} border border-cyan-500/20 h-full`}>
                <div className={`grid grid-cols-3 gap-2 mb-4 text-center ${isMobile ? 'text-xs' : 'text-sm'} font-semibold`}>
                  <div className="bg-cyan-600/30 rounded-lg py-2 px-1 text-cyan-300">% of Subscribers*</div>
                  <div className="bg-cyan-500/30 rounded-lg py-2 px-1 text-cyan-200"># of Revenue Earners</div>
                  <div className="bg-cyan-400/30 rounded-lg py-2 px-1 text-cyan-100">$ Per User (CAD)</div>
                </div>

                <div className="space-y-2">
                  {[
                    { pct: '0.5%', earners: '10,000', amount: '$10,000' },
                    { pct: '1.0%', earners: '20,000', amount: '$10,000' },
                    { pct: '1.5%', earners: '30,000', amount: '$10,000' },
                    { pct: '2.0%', earners: '40,000', amount: '$10,000' },
                    { pct: '2.5%', earners: '50,000', amount: '$10,000' },
                    { pct: '3.0%', earners: '60,000', amount: '$10,000' },
                    { pct: '3.5%', earners: '70,000', amount: '$10,000' },
                    { pct: '4.0%', earners: '80,000', amount: '$10,000', highlight: true },
                  ].map((row, idx) => (
                    <div key={idx} className={`grid grid-cols-3 gap-2 text-center ${row.highlight ? 'animate-pulse' : ''}`}>
                      <div className={`rounded-lg py-2.5 font-semibold ${isMobile ? 'text-xs' : ''} ${row.highlight ? 'bg-gradient-to-r from-cyan-500 to-cyan-400 text-gray-900' : 'bg-cyan-900/50 text-cyan-300'}`}>{row.pct}</div>
                      <div className={`rounded-lg py-2.5 font-medium ${isMobile ? 'text-xs' : ''} ${row.highlight ? 'bg-gradient-to-r from-cyan-400 to-cyan-300 text-gray-900' : 'bg-cyan-800/40 text-cyan-200'}`}>{row.earners}</div>
                      <div className={`rounded-lg py-2.5 font-bold ${isMobile ? 'text-xs' : ''} ${row.highlight ? 'bg-gradient-to-r from-cyan-300 to-emerald-300 text-gray-900' : 'bg-cyan-700/30 text-emerald-300'}`}>{row.amount}</div>
                    </div>
                  ))}
                </div>

                <div className={`mt-6 ${isMobile ? 'p-3' : 'p-4'} bg-gray-800/50 rounded-xl border border-gray-700/50`}>
                  <p className={`text-gray-300 ${isMobile ? 'text-xs' : 'text-sm'} text-center`}>
                    <span className="text-cyan-400">*</span> # of Paid Search.Inc Subscribers as a % of Market Share to Total Internet users on Earth
                  </p>
                  <p className={`text-yellow-400 ${isMobile ? 'text-xs' : 'text-sm'} text-center mt-2 font-medium`}>
                    Free users cannot participate.
                  </p>
                </div>
              </div>
            </div>

            {/* Right panel — Revenue Share Value Credits & rules */}
            <div className={isMobile ? '' : 'col-span-4'}>
              <div
                className={`relative rounded-2xl ${isMobile ? 'p-4' : 'p-6'} h-full flex flex-col overflow-hidden`}
                style={{
                  background: 'linear-gradient(160deg, rgba(6, 40, 60, 0.95) 0%, rgba(10, 22, 40, 0.98) 50%, rgba(15, 25, 45, 0.95) 100%)',
                  border: '1px solid rgba(0, 212, 255, 0.12)',
                }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-cyan-400/5 rounded-full blur-2xl"></div>

                <h3 className={`relative ${isMobile ? 'text-base' : 'text-xl'} font-bold text-white mb-5`}>
                  Search.Inc Revenue Share
                  <span className="block h-px w-16 bg-gradient-to-r from-cyan-400 to-transparent mt-2"></span>
                </h3>

                <p className={`relative text-cyan-300 font-bold ${isMobile ? 'text-xs' : 'text-sm'} uppercase tracking-wider mb-3`}>
                  Earn Value Credits by using Search.Inc
                </p>

                <div className={`relative space-y-2.5 mb-5 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  {[
                    { pts: '1,000', desc: 'Value Credits for paying your subscription' },
                    { pts: '1,000', desc: 'Value Credits for making us your default engine' },
                    { pts: '1,000', desc: 'Value Credits for setting and saving your accuracy control settings' },
                    { pts: '10', desc: 'Value Credits for writing a business review (tbc)' },
                    { pts: '10', desc: 'Value Credits for 10 consecutive searches and confirming if our returned results were correct or not (thumbs up or down) for each search' },
                  ].map((item, i) => (
                    <div key={i} className="grid gap-3" style={{ gridTemplateColumns: isMobile ? '42px 1fr' : '52px 1fr' }}>
                      <span className="text-cyan-400 font-bold text-right tabular-nums">{item.pts}</span>
                      <span className="text-gray-300 leading-snug">{item.desc}</span>
                    </div>
                  ))}
                </div>

                <div className="relative h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent my-3"></div>

                <p className={`relative text-cyan-300 font-bold ${isMobile ? 'text-xs' : 'text-sm'} uppercase tracking-wider mb-3`}>
                  Conditions
                </p>

                <ul className={`relative ${isMobile ? 'text-xs' : 'text-sm'} text-gray-300 space-y-2 mt-auto`}>
                  <li className="flex gap-2"><span className="text-cyan-500/60 flex-shrink-0">&#9670;</span><span>Users with the most Value Credits share in revenue pool</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500/60 flex-shrink-0">&#9670;</span><span>Users cannot share in consecutive thresholds but can in every other threshold</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500/60 flex-shrink-0">&#9670;</span><span>Market share determined by (TBC)</span></li>
                  <li className="flex gap-2"><span className="text-cyan-500/60 flex-shrink-0">&#9670;</span><span className="text-cyan-400 underline cursor-pointer">See Full Terms of Revenue Share here</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueShareTerms;
